import { Injectable } from '@angular/core';
import { APIClient } from 'src/app/core/api-client/contracts/api-client.service';
import { IResource } from '../models/resource.model';
import { map, switchMap, filter, tap, catchError, publishReplay, refCount } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { throwError, merge, of, Observable, Subject, BehaviorSubject } from 'rxjs';

@Injectable()
export class ResourcesService {

    private api = 'resources';
    private apiAuth = 'auth';
    private resources: any;
    private templateArgument = '{{#value}}';
    resourceKeyValue: any;

    bsObject = new BehaviorSubject({});

    constructor(
        private client: APIClient,
        private authService: AuthService
    ) { }

    resource(resourceId: string): string {
        return this.resources[resourceId] || '##' + resourceId + '##';
    }

    resourceFromTemplate(templateId: string, args: string[]) {
        const resource = this.resources[templateId];
        if (resource) {
            return args.reduce((acc, curr) => acc.replace(this.templateArgument, curr), resource);
        }
        return '##' + templateId + '##';
    }

    setResources(resources: any): void {
        this.resources = resources;
    }

    initializeResources(): Promise<boolean> {
        const callAPI = this.client.get<IResource[]>(`${this.api}/GetResourcesByUserSetting/`);
        //Validate if the user is logged in
        const isLoggedIn$ = this.IsLoggedIn().pipe(
            filter(Boolean),
            switchMap(() => callAPI),
            catchError((err) => {
                console.log(err);
                return throwError(err);
            }),
            map(this.mapResources),
            tap((resources) => {
                this.setResources(resources);
                this.resourceKeyValue = resources;
                this.bsObject.next(this.resourceKeyValue);
            }),
            map(() => true)
        );

        const isNotLoggedIn$ = of(true);

        return merge(isLoggedIn$, isNotLoggedIn$).toPromise();
    }

    changeCulture(id: number): Promise<boolean> {

        const callAPI = this.getResourceByCultureId(id)
        //Validate if the user is logged in
        const isLoggedIn$ = this.IsLoggedIn().pipe(
            filter(Boolean),
            switchMap(() => callAPI),
            catchError((err) => {
                console.log(err);
                return throwError(err);
            }),
            map(this.mapResources),
            tap((resources) => {
                this.setResources(resources);
                this.resourceKeyValue = resources;
                this.bsObject.next(this.resourceKeyValue);
            }),
            map(() => true)
        );

        const isNotLoggedIn$ = of(true);

        return merge(isLoggedIn$, isNotLoggedIn$).toPromise();
    }

    private mapResources(resources: IResource[]) {
        return resources.reduce((acc: any, prev: any) => {
            if (!acc[prev.name]) {
                acc[prev.name] = prev.value;
            }
            return acc;
        }, {});
    }

    getResourceByCultureId(id: number): Observable<IResource[]> {
        return this.client.get<IResource[]>(`${this.api}/GetResourcesBySelectedFacility/` + id);
    }

    getAllResourcesKeyValue(id: number) {
        return this.getResourceByCultureId(id).subscribe(result => {
            this.resourceKeyValue = Object.assign({}, ...result.map((x) => ({ [x.name]: x.value })));
            this.bsObject.next(this.resourceKeyValue);
        });
    }

    //Check if the user is logged in to prevent several calls to retrieve the resources
    IsLoggedIn(): Observable<boolean> {
        return this.client.get<boolean>(`${this.apiAuth}/IsLoggedIn`, undefined)
            .pipe(
                catchError(error => {
                    console.log(error);
                    return of(false);
                })
            );
    }
}
